export interface DialogueOption {
  text: string;
  nextDialogueId?: string;
  romancePoints?: number;
  action?: string;
}

export interface Dialogue {
  id: string;
  npcId: string;
  text: string;
  options: DialogueOption[];
  requiresRomanceLevel?: number;
}

export const DIALOGUES: Dialogue[] = [
  // Ei Bhaal - First Meeting
  {
    id: 'ei-first-meeting',
    npcId: 'ei-bhaal',
    text: "I've seen many adventurers pass through these lands, but there's something different about you. The darkness in this world hasn't consumed your spirit... yet.",
    options: [
      { text: 'What do you mean by that?', nextDialogueId: 'ei-first-follow' },
      { text: 'Are you offering to help me?', nextDialogueId: 'ei-offer-help' },
      { text: 'I should move on.', action: 'end_dialogue' },
    ],
  },
  {
    id: 'ei-first-follow',
    npcId: 'ei-bhaal',
    text: 'The world outside is harsh and unforgiving. Most lose themselves in the struggle. But you... you have potential. Perhaps we could aid one another on this journey.',
    options: [
      { text: 'I like the sound of that.', nextDialogueId: 'ei-bond-start', romancePoints: 5 },
      { text: 'What would that entail?', nextDialogueId: 'ei-explain' },
      { text: 'I work alone.', action: 'end_dialogue' },
    ],
  },
  {
    id: 'ei-bond-start',
    npcId: 'ei-bhaal',
    text: "Good. There's much to discover in this world, and even more to discover about ourselves. Come, walk with me.",
    options: [
      { text: 'Lead the way.', action: 'end_dialogue', romancePoints: 5 },
    ],
  },
  {
    id: 'ei-offer-help',
    npcId: 'ei-bhaal',
    text: 'Help is a strong word. Perhaps companionship is more accurate. The journey ahead is long, and the darkness is easier to bear when shared.',
    options: [
      { text: 'I accept your companionship.', nextDialogueId: 'ei-bond-start', romancePoints: 10 },
      { text: 'Interesting. Tell me more.', nextDialogueId: 'ei-explain' },
    ],
  },
  {
    id: 'ei-explain',
    npcId: 'ei-bhaal',
    text: 'Each location holds secrets and trials. Some will test your strength, others your resolve. Together, we could uncover truths long forgotten.',
    options: [
      { text: 'I like mysteries.', nextDialogueId: 'ei-bond-start', romancePoints: 5 },
      { text: 'Sounds dangerous.', action: 'end_dialogue' },
    ],
  },

  // Yìchén - First Meeting
  {
    id: 'yichen-first-meeting',
    npcId: 'yichen',
    text: "Well, well... a new face in Dark Hells. How delightful! I'm Yichén. I sense magic around you. Are you a practitioner?",
    options: [
      { text: 'I have some experience with magic.', nextDialogueId: 'yichen-magic-talk', romancePoints: 5 },
      { text: 'What do you care?', nextDialogueId: 'yichen-curious' },
      { text: 'I should go.', action: 'end_dialogue' },
    ],
  },
  {
    id: 'yichen-magic-talk',
    npcId: 'yichen',
    text: 'Excellent! Magic is the true language of this world. Those who master it can reshape reality itself. Perhaps I could teach you a thing or two.',
    options: [
      { text: "I'd love that.", nextDialogueId: 'yichen-bond-start', romancePoints: 10 },
      { text: 'What would you expect in return?', nextDialogueId: 'yichen-cost' },
    ],
  },
  {
    id: 'yichen-bond-start',
    npcId: 'yichen',
    text: "Perfect. I have a feeling we're going to be very... well acquainted. Meet me tonight? There's much I want to show you.",
    options: [
      { text: "I'll be there.", action: 'end_dialogue', romancePoints: 15 },
    ],
  },
  {
    id: 'yichen-curious',
    npcId: 'yichen',
    text: "Oh, don't be shy. I'm merely curious about interesting people. And you... you're definitely interesting.",
    options: [
      { text: "Maybe I'll let you find out.", nextDialogueId: 'yichen-bond-start', romancePoints: 10 },
      { text: 'Find someone else.', action: 'end_dialogue' },
    ],
  },
  {
    id: 'yichen-cost',
    npcId: 'yichen',
    text: "Ah, practical. I like that. Let's say... you help me with my research, and I'll share my knowledge. A fair exchange, don't you think?",
    options: [
      { text: 'Sounds reasonable.', nextDialogueId: 'yichen-bond-start', romancePoints: 10 },
      { text: 'What kind of research?', nextDialogueId: 'yichen-research' },
    ],
  },
  {
    id: 'yichen-research',
    npcId: 'yichen',
    text: "Ancient magic. The kind that can change destinies. Once you see what I'm working on, you'll understand why it matters so much.",
    options: [
      { text: "I'm intrigued.", nextDialogueId: 'yichen-bond-start', romancePoints: 10 },
      { text: 'Sounds dangerous.', action: 'end_dialogue' },
    ],
  },

  // Dollia - First Meeting
  {
    id: 'dollia-first-meeting',
    npcId: 'dollia',
    text: "Name's Dollia. I've got a sharp eye for danger and an even sharper trigger finger. You look like you can handle yourself. Interested in running jobs together?",
    options: [
      { text: 'What kind of jobs?', nextDialogueId: 'dollia-jobs-talk', romancePoints: 5 },
      { text: 'I work alone.', nextDialogueId: 'dollia-rival', romancePoints: 0 },
      { text: 'Not interested.', action: 'end_dialogue' },
    ],
  },
  {
    id: 'dollia-jobs-talk',
    npcId: 'dollia',
    text: 'The dangerous kind. The profitable kind. Bounties, treasure hunts, protecting merchants. I know all the best-paying gigs in Dark Hells.',
    options: [
      { text: "I'm in.", nextDialogueId: 'dollia-bond-start', romancePoints: 10 },
      { text: 'Sounds risky.', nextDialogueId: 'dollia-caution', romancePoints: 5 },
    ],
  },
  {
    id: 'dollia-bond-start',
    npcId: 'dollia',
    text: "Good. Partners then. Stick with me and you'll get rich... or die trying. Either way, it'll be exciting.",
    options: [
      { text: 'Looking forward to it.', action: 'end_dialogue', romancePoints: 10 },
    ],
  },
  {
    id: 'dollia-caution',
    npcId: 'dollia',
    text: "Risk? That's just part of the game. But I like that you're thinking clearly. Smart partners are the best kind.",
    options: [
      { text: 'Alright, count me in.', nextDialogueId: 'dollia-bond-start', romancePoints: 5 },
    ],
  },
  {
    id: 'dollia-rival',
    npcId: 'dollia',
    text: "Fair enough. But don't come crying to me when you need backup.",
    options: [
      { text: 'I can handle myself.', action: 'end_dialogue' },
    ],
  },

  // Alphonse - First Meeting
  {
    id: 'alphonse-first-meeting',
    npcId: 'alphonse',
    text: 'Greetings. I am Alphonse. Your aura intrigues me. There is depth within you that few possess. Shall we become acquainted?',
    options: [
      { text: "I'd like that.", nextDialogueId: 'alphonse-mystery', romancePoints: 5 },
      { text: 'What do you want?', nextDialogueId: 'alphonse-intentions', romancePoints: 0 },
      { text: 'Leave me be.', action: 'end_dialogue' },
    ],
  },
  {
    id: 'alphonse-mystery',
    npcId: 'alphonse',
    text: 'Excellent. The world has so many secrets, and I find myself drawn to those who seek to uncover them. Together, we might discover truths most will never know.',
    options: [
      { text: 'Lead the way.', nextDialogueId: 'alphonse-bond-start', romancePoints: 10 },
      { text: 'Tell me more first.', nextDialogueId: 'alphonse-explain', romancePoints: 5 },
    ],
  },
  {
    id: 'alphonse-bond-start',
    npcId: 'alphonse',
    text: 'A wise choice. I have much to share with you. In time, you will understand why fate brought us together.',
    options: [
      { text: 'I trust you.', action: 'end_dialogue', romancePoints: 10 },
    ],
  },
  {
    id: 'alphonse-explain',
    npcId: 'alphonse',
    text: 'I am searching for something ancient, something powerful. And I believe you may be instrumental in finding it. Will you help?',
    options: [
      { text: 'Yes.', nextDialogueId: 'alphonse-bond-start', romancePoints: 10 },
      { text: 'I need more information.', action: 'end_dialogue', romancePoints: 0 },
    ],
  },
  {
    id: 'alphonse-intentions',
    npcId: 'alphonse',
    text: 'What I want is companionship from one worthy of my time. You have potential. The question is whether you have the courage to walk my path.',
    options: [
      { text: 'I do.', nextDialogueId: 'alphonse-bond-start', romancePoints: 10 },
      { text: 'Maybe someone else.', action: 'end_dialogue' },
    ],
  },

  // Sylus - First Meeting
  {
    id: 'sylus-first-meeting',
    npcId: 'sylus',
    text: "Well, well... what do we have here? A new player in the game. I'm Sylus. And I must say, your energy is... intoxicating.",
    options: [
      { text: 'Thank you.', nextDialogueId: 'sylus-charm', romancePoints: 5 },
      { text: 'What do you want?', nextDialogueId: 'sylus-honest', romancePoints: 0 },
      { text: 'Back off.', action: 'end_dialogue' },
    ],
  },
  {
    id: 'sylus-charm',
    npcId: 'sylus',
    text: "I like you already. Most are so... predictable. But you? There's something genuinely interesting about you. Shall we get to know each other better?",
    options: [
      { text: "I'd enjoy that.", nextDialogueId: 'sylus-bond-start', romancePoints: 10 },
      { text: "What's your angle?", nextDialogueId: 'sylus-honest', romancePoints: 5 },
    ],
  },
  {
    id: 'sylus-bond-start',
    npcId: 'sylus',
    text: 'Delightful. I have a feeling you and I are going to have quite the... intimate connection.',
    options: [
      { text: 'Looking forward to it.', action: 'end_dialogue', romancePoints: 15 },
    ],
  },
  {
    id: 'sylus-honest',
    npcId: 'sylus',
    text: 'Straightforward. I appreciate that. My angle is simple: I enjoy the company of exceptional people. And you seem quite exceptional.',
    options: [
      { text: 'We should spend time together.', nextDialogueId: 'sylus-bond-start', romancePoints: 10 },
      { text: 'Stay away from me.', action: 'end_dialogue' },
    ],
  },
];